import React, {
  Dispatch,
  SetStateAction,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState
} from 'react'
import * as d3 from 'd3'
import figureSVG from '@/assets/images/AssociationPage/figure.svg'
import painterSVG from '@/assets/images/AssociationPage/painter.svg'

interface INode {
  x: number
  y: number
  id: number
  index: number
}

interface ILink {
  source: INode
  target: INode
  cnt: number
  index: number
}

const R = 30
const MAX_LINE_WIDTH = 20

const useForceGraph = (data, id2name, size) => {
  const [nodes, setNodes] = useState<INode[]>([])
  const [links, setLinks] = useState<ILink[]>([])
  const [max, setMax] = useState<number>(1)

  useEffect(() => {
    if (!size) return

    const linkCnt = {}
    let forceMax = 1

    data.forEach((d) => {
      const { p1, p2 } = d
      const key = p1 + '-' + p2
      if (linkCnt[key] === undefined) {
        linkCnt[key] = {
          source: p1,
          target: p2,
          cnt: 1
        }
      } else {
        linkCnt[key].cnt++
        forceMax = Math.max(forceMax, linkCnt[key].cnt)
      }
    })

    const forceLinks = Object.values(linkCnt)
    const forceNodes = Object.keys(id2name).map((d) => ({ id: +d }))

    const scale = d3
      .scaleLinear()
      .domain([1, forceMax])
      .range([size / 2 - R * 2, 4 * R])
    const forceLink = d3
      .forceLink(forceLinks)
      .id((d) => d.id)
      .distance((d) => scale(d.cnt))

    setTimeout(() => {
      try {
        d3.forceSimulation(forceNodes)
          .force('link', forceLink)
          .force('charge', d3.forceManyBody())
          .force('center', d3.forceCenter(size / 2, size / 2))
          .force('collide', d3.forceCollide().radius(R * 2))
          .force('x', d3.forceX())
          .force('y', d3.forceY())
          .stop()
          .tick(10000)
      } catch (e) {
        console.log(e)
      }

      setNodes(forceNodes as INode[])
      setLinks(forceLinks as ILink[])
      setMax(forceMax)
    }, 0)
  }, [data, id2name, size])

  return {
    nodes,
    links,
    scale: d3.scaleLog().domain([1, max]).range([2, MAX_LINE_WIDTH])
  }
}

type painterInfo = {
  data: [number, number, number] | null
  sum: number
}
export interface IGraph {
  data: {
    p1: number
    p2: number
  }[]
  id2name: {
    [key: string]: string
  }
  id2painter: {
    [key: string]: painterInfo
  }
  centerId: number | null // 只显示与这个人有关的边
  setCenterId: Dispatch<SetStateAction<number>>
  filterIds: number[]
  setFilterId: (s: string, t: string) => void
}

const PAINT_TYPES = ['山水', '人物', '花鸟']

const DEFAULT = d3.zoomIdentity.translate(0, 0).scale(1)
const useZoom = (ref) => {
  const [transform, setTransform] = useState<any>(DEFAULT)

  useEffect(() => {
    const zoom = d3
      .zoom()
      .scaleExtent([0.7, 32])
      .on('zoom', (e) => {
        setTransform(e.transform)
      })

    d3.select(ref.current).select('svg').call(zoom).transition().duration()
  }, [ref])

  return transform
}

const useHeight = (ele: HTMLElement | null) => {
  const [height, setHeight] = useState(0)

  useLayoutEffect(() => {
    if (!ele) return

    const handleElementChange = () => {
      const newHeight = ele.offsetHeight
      setHeight(newHeight)
    }

    handleElementChange()

    if (!window?.ResizeObserver) return
    const resizeObserver = new ResizeObserver(handleElementChange)
    resizeObserver.observe(ele)

    return () => {
      resizeObserver.disconnect()
    }
  }, [ele])

  return height
}

const Graph = ({
  data,
  id2name,
  id2painter,
  centerId,
  setCenterId,
  filterIds,
  setFilterId
}: IGraph) => {
  const $container = useRef<HTMLDivElement>(null)
  const height = useHeight($container.current)

  const { nodes, links, scale } = useForceGraph(data, id2name, height)
  const [info, setInfo] = useState<painterInfo | null>(null) // 山水，人物，花鸟
  const [position, setPosition] = useState<{ x: number; y: number } | null>(null)

  const transform = useZoom($container)

  const range = useMemo(() => {
    let minX = Number.MAX_VALUE
    let minY = Number.MAX_VALUE
    let maxX = Number.MIN_VALUE
    let maxY = Number.MIN_VALUE

    nodes.forEach((node) => {
      minX = Math.min(minX, node.x)
      minY = Math.min(minY, node.y)
      maxX = Math.max(maxX, node.x)
      maxY = Math.max(maxY, node.y)
    })

    return {
      x: [minX, maxX],
      y: [minY, maxY]
    }
  }, [nodes])

  const rScale = useMemo(() => {
    return d3
      .scaleLinear()
      .domain([0, d3.max(Object.values(id2painter).map((d) => d.sum))])
      .range([R, R * 1.5])
  }, [id2painter])

  const xScale = useMemo(() => {
    return transform.rescaleX(d3.scaleLinear().domain(range.x).range([0, height]).nice())
  }, [height, range.x, transform])

  const yScale = useMemo(() => {
    return transform.rescaleY(d3.scaleLinear().domain(range.y).range([0, height]).nice())
  }, [height, range.y, transform])

  const onClickPerson = useCallback(
    (id) => {
      setCenterId(id)
    },
    [setCenterId]
  )

  const onClickLink = useCallback(
    (source, target) => {
      setFilterId(source, target)
      console.log('sourceid' + source, 'targetid' + target)
    },
    [setFilterId]
  )

  const onHoverPerson = useCallback(
    (node) => {
      setInfo(id2painter[node.id])
      setPosition({
        x: xScale(node.x),
        y: yScale(node.y)
      })
    },
    [id2painter, xScale, yScale]
  )

  const onMouseOutPerson = useCallback(() => {
    setInfo(null)
    setPosition(null)
  }, [])

  const fontSize = useMemo(() => {
    return Math.min(20, 12 * Math.sqrt(transform.k))
  }, [transform.k])

  return (
    <div id='graph' ref={$container}>
      <svg width='100%' height={height + 'px'} viewBox={`0 0 ${height} ${height}`}>
        <g stroke='#ccc' opacity={0.6} cursor='pointer'>
          {links.map((link) => {
            if (link.source.id !== centerId && link.target.id !== centerId) {
              return null
            }

            const id1 = link.source.id + '-' + link.target.id
            const id2 = link.target.id + '-' + link.source.id
            const key = filterIds.join('-')
            return (
              <>
                {/* strokeWidth略宽达到stroke+fill效果 */}
                <line
                  key={link.index}
                  x1={xScale(link.source.x)}
                  y1={yScale(link.source.y)}
                  x2={xScale(link.target.x)}
                  y2={yScale(link.target.y)}
                  stroke={key === id1 || key === id2 ? 'var(--hightlight)' : '#000'}
                  strokeWidth={scale(link.cnt) * 1.2 + 1}
                  onClick={() => onClickLink(link.source.id, link.target.id)}
                />
                <line
                  key={link.index}
                  x1={xScale(link.source.x)}
                  y1={yScale(link.source.y)}
                  x2={xScale(link.target.x)}
                  y2={yScale(link.target.y)}
                  stroke={key === id1 || key === id2 ? 'var(--hightlight)' : '#ccc'}
                  strokeWidth={scale(link.cnt) * 1.2}
                  onClick={() => onClickLink(link.source.id, link.target.id)}
                />
              </>
            )
          })}
        </g>
        <g fontSize={fontSize} textAnchor='middle' cursor='pointer'>
          {nodes.map((node) => {
            const r = rScale(id2painter[node.id]?.sum || 0)
            return (
              <g
                key={node.id}
                transform={`translate(${xScale(node.x) - r / 2}, ${yScale(node.y) - r / 2})`}
                onClick={() => {
                  onClickPerson(node.id)
                  // console.log(node.id)
                  // onClickLink(node.id, centerId)
                }}
                {...(id2painter[node.id]
                  ? {
                      onMouseOver: () => onHoverPerson(node),
                      onMouseOut: onMouseOutPerson
                    }
                  : {})}
              >
                <image href={id2painter[node.id] ? painterSVG : figureSVG} width={r} height={r} />)
                <text dx={r / 2} dy={r + fontSize}>
                  {id2name[node.id]}
                </text>
              </g>
            )
          })}
        </g>
      </svg>
      {!!position && (
        <div
          className='info'
          style={{
            transform: `translate(${position.x - height / 2 + 50}px,${
              position.y - height / 2 + 60
            }px)`
          }}
        >
          {info && (
            <>
              <p>总数: {info.sum}</p>
              {PAINT_TYPES.map((t, i) => (
                <p key={t}>
                  {t}: {info?.data?.[i]}
                </p>
              ))}
            </>
          )}
        </div>
      )}
    </div>
  )
}

export default Graph
